import { useMemo, useState } from "react";
import { getJobSummary } from "@/data/communicationEngine";
import { useParams, useSearchParams } from "react-router-dom";
import { useAppData } from "@/data/AppDataContext";
import { useBranding } from "@/data/BrandingContext";
import { useProductOptions } from "@/data/ProductOptionsContext";
import { PortalLayout } from "@/components/portal/PortalLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  CheckCircle, Clock, Calendar, FileText, DollarSign,
  HelpCircle, Home, Layers, Send, ShieldCheck, Wrench,
  ChevronRight, Package, MessageSquare, AlertCircle,
  Bell,
} from "lucide-react";
import type { Job } from "@/data/types";
import type { Quote, ContractDocument } from "@/data/quoteTypes";
import { isWonStatus } from "@/data/quoteTypes";
import type { ChangeOrder } from "@/data/changeOrderTypes";
import { buildLineageFromRecords } from "@/data/versioningSystem";
import { VersionTimeline } from "@/components/VersionTimeline";
import type { ProjectConfiguration } from "@/components/ProjectConfigurator";
import { buildMilestoneTimeline, getCustomerStatusDescription, type MilestoneEntry } from "@/data/customerNotifications";
import { formatAddress } from "@/lib/addressUtils";
import { IssueService } from "@/services/issue.service";
import { JobService } from "@/services/job.service";

/* ══════════════════════════════════════════════════════════
   Customer-safe status translation layer
   ══════════════════════════════════════════════════════════ */
type CustomerStatus = "Approved" | "Scheduled" | "In Progress" | "Completed";

function getCustomerStatus(job: Job): CustomerStatus {
  if (job.currentStage === "Completed") return "Completed";
  if (job.currentStage === "In Progress" || job.currentStage === "QA Review") return "In Progress";
  if (job.scheduledInstallDate) return "Scheduled";
  return "Approved";
}

function getCustomerStatusFromQuote(quote: Quote): CustomerStatus {
  if ((isWonStatus(quote.status) || isWonStatus(quote.status)) && quote.convertedJobId) return "Approved";
  return "Approved";
}

const STATUS_CONFIG: Record<CustomerStatus, { icon: typeof CheckCircle; color: string; bg: string; label: string }> = {
  Approved: { icon: ShieldCheck, color: "text-primary", bg: "bg-primary/10", label: "Project Approved" },
  Scheduled: { icon: Calendar, color: "text-info", bg: "bg-info/10", label: "Installation Scheduled" },
  "In Progress": { icon: Wrench, color: "text-warning", bg: "bg-warning/10", label: "Installation In Progress" },
  Completed: { icon: CheckCircle, color: "text-success", bg: "bg-success/10", label: "Project Complete" },
};

const STATUS_STEPS: CustomerStatus[] = ["Approved", "Scheduled", "In Progress", "Completed"];

/* ══════════════════════════════════════════════════════════
   Helpers – translate customer-friendly issue status
   ══════════════════════════════════════════════════════════ */
function friendlyIssueStatus(s: string): string {
  switch (s) {
    case "Open": return "Received";
    case "In Progress": return "Being Reviewed";
    case "Resolved": return "Resolved";
    case "Closed": return "Closed";
    default: return s;
  }
}

/* ══════════════════════════════════════════════════════════
   Portal Component
   ══════════════════════════════════════════════════════════ */
export default function CustomerPortal() {
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const { jobs, issues, setIssues, quotes } = useAppData();
  const { branding } = useBranding();
  const { options: allOptions } = useProductOptions();

  const token = searchParams.get("token");

  /* ── Resolve the job & linked quote ── */
  const { job, linkedQuote } = useMemo(() => {
    let foundJob: Job | undefined;
    if (jobId) foundJob = jobs.find((j) => j.id === jobId);
    else if (token) foundJob = jobs.find((j) => j.id === token);
    else foundJob = jobs.find((j) => j.currentStage === "In Progress") || jobs[0];

    // Find linked quote (via convertedFromQuoteId on job, or convertedJobId on quote)
    let foundQuote: Quote | undefined;
    if (foundJob) {
      foundQuote = quotes.find(
        (q) => q.convertedJobId === foundJob!.id || (foundJob!.convertedFromQuoteId && q.id === foundJob!.convertedFromQuoteId),
      );
    } else if (jobId || token) {
      // Maybe the ID is a quote ID — show quote-only portal
      const targetId = jobId || token;
      foundQuote = quotes.find((q) => q.id === targetId);
    }
    return { job: foundJob, linkedQuote: foundQuote };
  }, [jobs, quotes, jobId, token]);

  const [helpType, setHelpType] = useState("");
  const [helpMessage, setHelpMessage] = useState("");
  const [helpSubmitted, setHelpSubmitted] = useState(false);

  // Customer-submitted issues (must be before early return)
  const customerIssues = useMemo(() => {
    if (!job) return [];
    return issues.filter(
      (i) => i.jobId === job.id && (i.source === "Customer" || i.description?.includes("[Customer Portal]")),
    );
  }, [issues, job]);

  /* ── No data found ── */
  if (!job && !linkedQuote) {
    return (
      <PortalLayout>
        <div className="py-20 text-center">
          <Home className="h-12 w-12 text-muted-foreground/40 mx-auto mb-4" />
          <h2 className="text-lg font-semibold mb-2">Project Not Found</h2>
          <p className="text-sm text-muted-foreground">Please check your link or contact us for assistance.</p>
        </div>
      </PortalLayout>
    );
  }

  /* ══════════════════════════════════════════════════════════
     Derive all display data from job OR quote (job takes priority)
     ══════════════════════════════════════════════════════════ */
  const customerName = job?.customerName || linkedQuote?.customerName || "Customer";
  const projectType = job?.projectType || (linkedQuote?.projectConfig?.selectedVerticals?.[0]) || "Home Improvement";
  const address = job ? formatAddress(job.address) : linkedQuote?.customerAddress || "";
  const scopeSummary = job?.scopeSummary || linkedQuote?.scopeOfWork || "";

  // Status
  const status: CustomerStatus = job ? getCustomerStatus(job) : getCustomerStatusFromQuote(linkedQuote!);
  const statusConfig = STATUS_CONFIG[status];
  const StatusIcon = statusConfig.icon;
  const currentStepIndex = STATUS_STEPS.indexOf(status);

  // Project config — prefer job's config, fall back to quote's
  const projectConfig: ProjectConfiguration | undefined = job?.projectConfig || linkedQuote?.projectConfig;

  // Contract & change orders — from job first, then quote
  const contractDocument: ContractDocument | undefined = job?.contractDocument || linkedQuote?.contract;
  const changeOrders: ChangeOrder[] = job?.changeOrders || [];

  // Documents list (customer-safe only)
  const customerDocuments: { label: string; detail: string; signed: boolean; icon: typeof FileText }[] = [];
  if (contractDocument) {
    customerDocuments.push({
      label: "Signed Contract",
      detail: contractDocument.homeownerSignedAt
        ? `Signed ${new Date(contractDocument.homeownerSignedAt).toLocaleDateString()}`
        : "Pending signature",
      signed: !!contractDocument.homeownerSignedAt,
      icon: FileText,
    });
  }
  changeOrders
    .filter((co) => co.status === "Signed" || co.status === "Pending Review")
    .forEach((co) => {
      customerDocuments.push({
        label: `Change Order #${co.orderNumber}`,
        detail: co.summary,
        signed: co.status === "Signed",
        icon: FileText,
      });
    });

  // Financials — use actual quote deposit data when available
  const contractValue = (() => {
    if (contractDocument?.contractTotal) return contractDocument.contractTotal;
    if (job?.financials?.contractValue) return job.financials.contractValue;
    if (linkedQuote?.totalEstimate) return linkedQuote.totalEstimate;
    return 0;
  })();

  // Change order adjustments (signed only)
  const changeOrderTotal = changeOrders
    .filter((co) => co.status === "Signed")
    .reduce((s, co) => s + co.changeAmount, 0);

  const currentContractValue = contractValue + changeOrderTotal;

  // Payment plan aware deposit/balance derivation
  const plan = job?.financials?.paymentPlan;
  const depositInfo = linkedQuote?.deposit;

  const depositCollected = plan
    ? plan.lineItems.some(li => li.kind === "deposit" && (li.status === "paid" || li.status === "waived"))
    : !!depositInfo?.amount || (job?.financials?.paymentStatus !== "Not Invoiced" && job?.financials?.paymentStatus !== undefined);

  const depositAmount = plan
    ? (plan.lineItems.find(li => li.kind === "deposit")?.amount ?? 0)
    : depositInfo?.amount
      ? Math.round((depositInfo.amount / 100) * contractValue)
      : depositCollected ? Math.round(contractValue * 0.5) : 0;

  const depositMethod = depositInfo?.method || (plan?.lineItems.find(li => li.kind === "deposit")?.method) || "";

  const totalPaid = plan
    ? plan.lineItems.filter(li => li.status === "paid" || li.status === "waived").reduce((s, li) => s + li.amount, 0)
    : depositCollected ? depositAmount : 0;

  const remainingBalance = currentContractValue - totalPaid;

  // Payment status label for customer
  const customerPaymentStatus = plan
    ? plan.lineItems.every(li => li.status === "paid" || li.status === "waived")
      ? "Paid in Full"
      : remainingBalance > 0
        ? "Balance Due"
        : "Paid"
    : depositCollected ? "Financially Cleared" : "Payment Pending";

  const customerBalanceLabel = plan
    ? plan.lineItems.some(li => li.dueAt === "post_install" && li.status !== "paid" && li.status !== "waived")
      ? "Due at completion"
      : remainingBalance > 0
        ? "Due before scheduling"
        : "No balance due"
    : "Due at completion";

  const installDate = job?.scheduledInstallDate || "";
  const arrivalTime = job?.scheduledStartTime || "";

  /* ── Support form handler ── */
  async function handleHelpSubmit() {
    if (!helpMessage.trim()) {
      toast.error("Please describe your question or concern.");
      return;
    }
    const targetJobId = job?.id || linkedQuote?.convertedJobId || linkedQuote?.id || "UNKNOWN";
    const newIssue = {
      id: `ISS-PORTAL-${Date.now()}`,
      jobId: targetJobId,
      issueType: "Customer Complaint" as const,
      severity: "Medium" as const,
      description: `[Customer Portal] ${helpType || "General"}: ${helpMessage}`,
      status: "Open" as const,
      dateCreated: new Date().toISOString().split("T")[0],
      source: "Customer" as const,
      photo: "",
      owner: customerName,
    };

    try {
      // Persist to database
      const { data: createdIssue, error } = await IssueService.create(
        { ...newIssue, id: `ISS-PORTAL-${Date.now()}` },
        {
          customerName,
          jobNumber: job?.jobNumber || linkedQuote?.quote_number || targetJobId,
        }
      );

      if (error || !createdIssue) {
        throw error || new Error("Failed to create issue");
      }

      // Update job issueFlag
      if (job) {
        await JobService.update(job.id, { issueFlag: true });
      }

      // Update local state
      setIssues((prev) => [...prev, createdIssue]);
      setHelpSubmitted(true);
      toast.success("Your request has been submitted. We'll be in touch shortly!");
    } catch (err) {
      console.error("Failed to submit request:", err);
      toast.error("Failed to submit request. Please try again.");
    }
  }

  return (
    <PortalLayout>
      <div className="space-y-6">
        {/* ── Welcome ── */}
        <div>
          <h2 className="text-xl font-bold tracking-tight">Welcome, {customerName.split(" ")[0]}</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Here's the latest on your project.</p>
          {job && (
            <div className="mt-2 px-3 py-2 rounded-lg bg-muted/40 border border-border/40">
              <p className="text-sm font-medium text-foreground">{getJobSummary(job)}</p>
            </div>
          )}
        </div>

        {/* ── Status Progress ── */}
        <Card className={`${statusConfig.bg} border-2 shadow-sm`}>
          <CardContent className="p-5">
            <div className="flex items-center gap-3 mb-5">
              <div className={`h-11 w-11 rounded-full ${statusConfig.bg} flex items-center justify-center`}>
                <StatusIcon className={`h-6 w-6 ${statusConfig.color}`} />
              </div>
              <div>
                <p className={`text-lg font-bold ${statusConfig.color}`}>{statusConfig.label}</p>
                <p className="text-xs text-muted-foreground">{projectType}</p>
              </div>
            </div>

            {/* Progress Steps */}
            <div className="flex items-center gap-0">
              {STATUS_STEPS.map((step, i) => {
                const isCompleted = i < currentStepIndex;
                const isCurrent = i === currentStepIndex;
                const StepIcon = STATUS_CONFIG[step].icon;
                return (
                  <div key={step} className="flex items-center flex-1">
                    <div className="flex flex-col items-center flex-1">
                      <div className={`h-9 w-9 rounded-full flex items-center justify-center border-2 transition-all ${
                        isCompleted ? "bg-success border-success" :
                        isCurrent ? "border-primary bg-primary/10" :
                        "border-muted bg-muted/30"
                      }`}>
                        {isCompleted ? (
                          <CheckCircle className="h-4.5 w-4.5 text-success-foreground" />
                        ) : (
                          <StepIcon className={`h-4 w-4 ${isCurrent ? "text-primary" : "text-muted-foreground/50"}`} />
                        )}
                      </div>
                      <p className={`text-[10px] mt-1.5 font-medium text-center leading-tight ${
                        isCompleted ? "text-success" : isCurrent ? "text-primary" : "text-muted-foreground/50"
                      }`}>{step}</p>
                    </div>
                    {i < STATUS_STEPS.length - 1 && (
                      <div className={`h-0.5 flex-1 -mt-4 mx-1 rounded-full ${
                        isCompleted ? "bg-success" : "bg-muted"
                      }`} />
                    )}
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* ── Status Description ── */}
        {job && (
          <div className="rounded-lg border bg-muted/20 px-4 py-3">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {getCustomerStatusDescription(job)}
            </p>
          </div>
        )}

        {/* ── Progress Timeline ── */}
        {job && (() => {
          const milestones = buildMilestoneTimeline(job, changeOrders);
          const reachedMilestones = milestones.filter(m => m.reached);
          if (reachedMilestones.length === 0) return null;
          return (
            <Card>
              <CardHeader className="pb-2 px-5 pt-5">
                <CardTitle className="text-sm font-semibold flex items-center gap-2">
                  <Bell className="h-4 w-4 text-primary" /> Project Updates
                </CardTitle>
              </CardHeader>
              <CardContent className="px-5 pb-5">
                <div className="space-y-0">
                  {milestones.map((milestone, i) => {
                    const isLast = i === milestones.length - 1;
                    return (
                      <div key={`${milestone.type}-${i}`} className="relative flex gap-3 pb-3">
                        {/* Connector */}
                        <div className="flex flex-col items-center">
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center shrink-0 ${
                            milestone.reached
                              ? "bg-primary/10 text-primary"
                              : "bg-muted/40 text-muted-foreground/40"
                          }`}>
                            {milestone.reached ? (
                              <CheckCircle className="h-3.5 w-3.5" />
                            ) : (
                              <Clock className="h-3 w-3" />
                            )}
                          </div>
                          {!isLast && (
                            <div className={`w-px flex-1 mt-1 ${milestone.reached ? "bg-primary/20" : "bg-border"}`} />
                          )}
                        </div>
                        {/* Content */}
                        <div className="flex-1 min-w-0 pt-0.5">
                          <p className={`text-xs font-semibold ${milestone.reached ? "text-foreground" : "text-muted-foreground/50"}`}>
                            {milestone.title}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5">{milestone.description}</p>
                          {milestone.reached && milestone.timestamp && (
                            <p className="text-[10px] text-muted-foreground/60 mt-1">
                              {new Date(milestone.timestamp).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                            </p>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          );
        })()}

        {/* ── Project Details ── */}
        <Card>
          <CardHeader className="pb-2 px-5 pt-5">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Layers className="h-4 w-4 text-primary" /> Project Details
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="p-3 rounded-lg bg-muted/30 border">
                <p className="text-[10px] text-muted-foreground uppercase font-medium mb-0.5">Project Type</p>
                <p className="text-sm font-semibold">{projectType}</p>
              </div>
              {address && (
                <div className="p-3 rounded-lg bg-muted/30 border">
                  <p className="text-[10px] text-muted-foreground uppercase font-medium mb-0.5">Address</p>
                  <p className="text-sm font-semibold">{address}</p>
                </div>
              )}
              {scopeSummary && (
                <div className="p-3 rounded-lg bg-muted/30 border sm:col-span-2">
                  <p className="text-[10px] text-muted-foreground uppercase font-medium mb-0.5">What's Included</p>
                  <p className="text-sm">{scopeSummary}</p>
                </div>
              )}
              {projectConfig && (
                <>
                  {projectConfig.bathroomSystemType && (
                    <div className="p-3 rounded-lg bg-muted/30 border">
                      <p className="text-[10px] text-muted-foreground uppercase font-medium mb-0.5">System</p>
                      <p className="text-sm font-semibold">{projectConfig.bathroomSystemType}</p>
                    </div>
                  )}
                   {projectConfig.selectedWallSystem && (
                     <div className="p-3 rounded-lg bg-muted/30 border">
                       <p className="text-[10px] text-muted-foreground uppercase font-medium mb-0.5">Wall System</p>
                       <p className="text-sm font-semibold">{allOptions.find(o => o.id === projectConfig.selectedWallSystem)?.displayName || projectConfig.selectedWallSystem}</p>
                     </div>
                   )}
                   {projectConfig.selectedGlass && (
                     <div className="p-3 rounded-lg bg-muted/30 border">
                       <p className="text-[10px] text-muted-foreground uppercase font-medium mb-0.5">Glass</p>
                       <p className="text-sm font-semibold">{allOptions.find(o => o.id === projectConfig.selectedGlass)?.displayName || projectConfig.selectedGlass}</p>
                     </div>
                   )}
                  {projectConfig.bathroomBaseSize && (
                    <div className="p-3 rounded-lg bg-muted/30 border">
                      <p className="text-[10px] text-muted-foreground uppercase font-medium mb-0.5">Base Size</p>
                      <p className="text-sm font-semibold">{projectConfig.bathroomBaseSize}</p>
                    </div>
                  )}
                  {projectConfig.primaryMaterial && (
                    <div className="p-3 rounded-lg bg-muted/30 border">
                      <p className="text-[10px] text-muted-foreground uppercase font-medium mb-0.5">Material</p>
                      <p className="text-sm font-semibold">{projectConfig.primaryMaterial}</p>
                    </div>
                  )}
                  {projectConfig.colorSelection && (
                    <div className="p-3 rounded-lg bg-muted/30 border">
                      <p className="text-[10px] text-muted-foreground uppercase font-medium mb-0.5">Color</p>
                      <p className="text-sm font-semibold">{projectConfig.colorSelection}</p>
                    </div>
                  )}
                   {projectConfig.selectedAddOns && projectConfig.selectedAddOns.length > 0 && (
                     <div className="p-3 rounded-lg bg-muted/30 border sm:col-span-2">
                       <p className="text-[10px] text-muted-foreground uppercase font-medium mb-0.5">Add-Ons</p>
                       <p className="text-sm">{projectConfig.selectedAddOns.map(id => allOptions.find(o => o.id === id)?.displayName || id).join(", ")}</p>
                     </div>
                   )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* ── Schedule ── */}
        <Card>
          <CardHeader className="pb-2 px-5 pt-5">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Calendar className="h-4 w-4 text-primary" /> Schedule
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            {installDate ? (
              <div className="flex items-center gap-4 p-4 rounded-lg bg-info/5 border border-info/15">
                <Calendar className="h-8 w-8 text-info shrink-0" />
                <div>
                  <p className="text-lg font-bold">{new Date(installDate).toLocaleDateString("en-US", {
                    weekday: "long", month: "long", day: "numeric", year: "numeric",
                  })}</p>
                  {arrivalTime && (
                    // <p className="text-sm text-muted-foreground">Estimated arrival: {arrivalTime}</p>
                    <p className="text-sm text-muted-foreground">
                      Estimated arrival:{" "}
                      {new Date(arrivalTime).toLocaleTimeString("en-US", {
                        hour: "numeric",
                        minute: "2-digit",
                        hour12: true,
                      })}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">Our team will contact you the day before to confirm.</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-3 p-4 rounded-lg bg-muted/30 border">
                <Clock className="h-6 w-6 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">Installation date pending</p>
                  <p className="text-xs text-muted-foreground">We'll notify you once your installation is scheduled.</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Documents ── */}
        <Card>
          <CardHeader className="pb-2 px-5 pt-5">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <FileText className="h-4 w-4 text-primary" /> Documents
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            {customerDocuments.length > 0 ? (
              <div className="space-y-2">
                {customerDocuments.map((doc, i) => (
                  <div key={i} className="flex items-center justify-between p-3 rounded-lg border hover:bg-muted/20 transition-colors">
                    <div className="flex items-center gap-3">
                      <doc.icon className={`h-4 w-4 ${doc.signed ? "text-primary" : "text-muted-foreground"}`} />
                      <div>
                        <p className="text-sm font-medium">{doc.label}</p>
                        <p className="text-[10px] text-muted-foreground">{doc.detail}</p>
                      </div>
                    </div>
                    <Badge variant={doc.signed ? "default" : "secondary"} className="text-[9px]">
                      {doc.signed ? "Signed" : "Pending"}
                    </Badge>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/30 border">
                <FileText className="h-4 w-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground">No documents available yet.</p>
              </div>
            )}
            {/* Version History (compact for portal) */}
            {contractDocument && projectConfig && (() => {
              const lineage = buildLineageFromRecords(
                linkedQuote?.id || job?.id || "",
                projectConfig,
                currentContractValue,
                contractDocument,
                changeOrders,
              );
              if (lineage.versions.length <= 1) return null;
              return (
                <div className="mt-4 pt-4 border-t">
                  <VersionTimeline lineage={lineage} compact />
                </div>
              );
            })()}
          </CardContent>
        </Card>

        {/* ── Payment Summary ── */}
        <Card>
          <CardHeader className="pb-2 px-5 pt-5">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <DollarSign className="h-4 w-4 text-primary" /> Payment Summary
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div className="p-4 rounded-lg bg-muted/30 border text-center">
                <p className="text-[10px] text-muted-foreground uppercase font-medium mb-1">Project Total</p>
                <p className="text-xl font-bold">${currentContractValue.toLocaleString()}</p>
                {changeOrderTotal !== 0 && (
                  <p className="text-[9px] text-muted-foreground">
                    Includes {changeOrderTotal > 0 ? "+" : ""}${changeOrderTotal.toLocaleString()} in change orders
                  </p>
                )}
              </div>
              <div className={`p-4 rounded-lg border text-center ${depositCollected ? "bg-success/5 border-success/20" : "bg-warning/5 border-warning/20"}`}>
                <p className="text-[10px] text-muted-foreground uppercase font-medium mb-1">Deposit</p>
                <p className={`text-xl font-bold ${depositCollected ? "text-success" : "text-warning"}`}>
                  {depositCollected ? `$${depositAmount.toLocaleString()}` : "Pending"}
                </p>
                {depositCollected && (
                  <p className="text-[9px] text-success">
                    Received{depositMethod ? ` via ${depositMethod}` : ""}
                  </p>
                )}
              </div>
              <div className={`p-4 rounded-lg border text-center ${remainingBalance <= 0 ? "bg-success/5 border-success/20" : "bg-muted/30"}`}>
                <p className="text-[10px] text-muted-foreground uppercase font-medium mb-1">Balance</p>
                <p className={`text-xl font-bold ${remainingBalance <= 0 ? "text-success" : ""}`}>
                  {remainingBalance <= 0 ? "Paid ✓" : `$${remainingBalance.toLocaleString()}`}
                </p>
                <p className="text-[9px] text-muted-foreground">{customerBalanceLabel}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* ── Your Requests (issue history) ── */}
        {customerIssues.length > 0 && (
          <Card>
            <CardHeader className="pb-2 px-5 pt-5">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <MessageSquare className="h-4 w-4 text-primary" /> Your Requests
              </CardTitle>
            </CardHeader>
            <CardContent className="px-5 pb-5">
              <div className="space-y-2">
                {customerIssues.map((issue) => {
                  const friendlyStatus = friendlyIssueStatus(issue.status);
                  const isResolved = issue.status === "Resolved" || issue.status === "Closed";
                  return (
                    <div key={issue.id} className="flex items-start justify-between p-3 rounded-lg border gap-3">
                      <div className="flex items-start gap-3 min-w-0">
                        {isResolved ? (
                          <CheckCircle className="h-4 w-4 text-success shrink-0 mt-0.5" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-warning shrink-0 mt-0.5" />
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate">
                            {issue.description.replace("[Customer Portal] ", "").replace(/^[^:]+:\s*/, "")}
                          </p>
                          <p className="text-[10px] text-muted-foreground">
                            Submitted {issue.dateCreated}
                          </p>
                        </div>
                      </div>
                      <Badge variant={isResolved ? "default" : "secondary"} className="text-[9px] shrink-0">
                        {friendlyStatus}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* ── Support / Help ── */}
        <Card id="support">
          <CardHeader className="pb-2 px-5 pt-5">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <HelpCircle className="h-4 w-4 text-primary" /> Need Help?
            </CardTitle>
          </CardHeader>
          <CardContent className="px-5 pb-5">
            {helpSubmitted ? (
              <div className="py-8 text-center">
                <CheckCircle className="h-10 w-10 text-success mx-auto mb-3" />
                <h3 className="text-sm font-semibold mb-1">Request Submitted</h3>
                <p className="text-xs text-muted-foreground">Our team will review your request and get back to you shortly.</p>
                <Button variant="outline" size="sm" className="mt-4" onClick={() => { setHelpSubmitted(false); setHelpMessage(""); setHelpType(""); }}>
                  Submit Another Request
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                <p className="text-xs text-muted-foreground">
                  Have a question about your project? Let us know and we'll get back to you.
                </p>
                <Select value={helpType} onValueChange={setHelpType}>
                  <SelectTrigger className="h-9 text-xs">
                    <SelectValue placeholder="What is this about?" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="scheduling">Scheduling Question</SelectItem>
                    <SelectItem value="materials">Materials / Design Question</SelectItem>
                    <SelectItem value="payment">Payment Question</SelectItem>
                    <SelectItem value="issue">Report an Issue</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <Textarea
                  value={helpMessage}
                  onChange={(e) => setHelpMessage(e.target.value)}
                  placeholder="Tell us how we can help..."
                  className="text-sm min-h-[80px]"
                />
                <Button onClick={handleHelpSubmit} className="w-full sm:w-auto gap-2">
                  <Send className="h-3.5 w-3.5" /> Submit Request
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ── Contact Info ── */}
        <div className="text-center py-4">
          <p className="text-xs text-muted-foreground">
            Questions? Call us at <span className="font-medium text-foreground">(813) 555-0100</span> or email{" "}
            <span className="font-medium text-primary">support@ccchomeimprovement.com</span>
          </p>
        </div>
      </div>
    </PortalLayout>
  );
}
